<template>
  <div>
    <span v-if="mostRecentOutbound && isVisible && !isDisabled">
      <v-icon
        v-if="mostRecentOutbound.status === finished"
        :title="tkey('recentlyUploadedOn')"
        size="20"
        class="icon finished"
        >mdi-check-circle</v-icon
      >
      <v-icon
        v-if="mostRecentOutbound.status === error"
        :title="tkey('errorDuringUpload')"
        size="20"
        class="icon error-icon"
        >mdi-alert</v-icon
      >
      {{ formatDate(mostRecentOutboundTimestamp, getDateFormats.longWithFullTime) }}
    </span>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { get, size } from 'lodash';
import { mapState } from 'pinia';
import ftpUploadStatuses from '@enums/ftp-upload-statuses';
import extractTypes from '@enums/extract-types';
import { useContextStore } from '@/stores/context';
import { useCompoundExtractStore } from '@/stores/compound-extract';
import { useInternationalization } from '@/composables/use-internationalization';
import { useDayjs } from '@/composables/use-dayjs';
import { usePermission } from '@/composables/use-permission';

dayjs.extend(utc);

const { AOC } = extractTypes;
const { finished, error } = ftpUploadStatuses;

export default {
  setup() {
    const { tkey } = useInternationalization('compoundExtractsPage.uploadButton');
    const { formatDate } = useDayjs();
    const { canEditCompoundExtracts } = usePermission();

    return {
      tkey,
      formatDate,
      canEditCompoundExtracts,
    };
  },

  data() {
    return {
      loading: false,
      dayjs,
      finished,
      error,
    };
  },

  computed: {
    ...mapState(useContextStore, ['getDateFormats']),
    ...mapState(useCompoundExtractStore, ['getExtractById', 'extractSeriesIdBySettingId']),

    extractId() {
      return get(this.params, 'extractId');
    },

    extract() {
      return this.getExtractById(this.extractId) || {};
    },

    outboundType() {
      return get(this.params, 'outboundType');
    },

    outboundHistory() {
      return get(this.params, 'outboundHistory', null);
    },

    mostRecentOutbound() {
      const outboundHistoryByType = this.getOutboundHistoryByType();
      if (!outboundHistoryByType || !outboundHistoryByType.length) return null;

      const mostRecentOutboundByType = outboundHistoryByType.reduce((a, b) => {
        return new Date(a.timestamp) > new Date(b.timestamp) ? a : b;
      });

      return mostRecentOutboundByType;
    },

    mostRecentOutboundTimestamp() {
      return dayjs.utc(this.mostRecentOutbound.timestamp);
    },

    isVisible() {
      return this.extract.extractType === AOC;
    },

    isDisabled() {
      return (
        !this.canEditCompoundExtracts ||
        !this.extract._id ||
        !this.extract.extractSettingsId ||
        !size(this.extract.blobId)
      );
    },
  },

  methods: {
    getOutboundHistoryByType() {
      if (!this.outboundHistory) return null;
      return this.outboundHistory.filter(element => element.type === this.outboundType);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

a {
  text-decoration: none;
}

.icon {
  margin: 0 8px !important;
}

.finished {
  color: $assortment-positive-action-colour;
}

.error-icon {
  color: $assortment-negative-action-colour;
}
</style>
